<template>
  <HomeExternalCardsAndClassesCard
    title="My Cards and Classes"
    :cards="groups.mine.cards"
    :scheduled-classes="groups.mine.scheduledClasses"
    :awaiting-scheduling="groups.mine.awaitingScheduling"
    :has-load-error="hasLoadError"
  />
  <HomeExternalCardsAndClassesCard
    title="Others' Cards and Classes"
    :cards="groups.others.cards"
    :scheduled-classes="groups.others.scheduledClasses"
    :awaiting-scheduling="groups.others.awaitingScheduling"
  />
</template>

<script lang="ts">
import HomeExternalCardsAndClassesCard from "@/components/HomeExternalCardsAndClassesCard.vue";
import {
  buildCardsAndClassesGroups,
  classRegistrationFormMurns,
  emptyCardsAndClassesGroups,
} from "@/components/homeExternalCardsAndClasses";
import { ApplicationSettingKeys } from "@/settings/applicationSetting";
import { ensureApplicationSettings } from "@/settings/useApplicationSettings";
import { getDustCards } from "@/views/dustCard/dustCardApi";
import dustCardService from "@/views/dustCard/dustCardService";
import { getDustClasses } from "@/views/dustClass/dustClassApi";
import dustClassService from "@/views/dustClass/dustClassService";
import { getElectronicForms } from "@/views/electronicForm/electronicFormApi";
import electronicFormService from "@/views/electronicForm/electronicFormService";
import { getDustMonitorCards } from "@/views/dustMonitorCard/dustMonitorCardApi";
import dustMonitorCardService from "@/views/dustMonitorCard/dustMonitorCardService";
import { getDustMonitorClasses } from "@/views/dustMonitorClass/dustMonitorClassApi";
import dustMonitorClassService from "@/views/dustMonitorClass/dustMonitorClassService";
import { adaptPagedResponse } from "@modernary/mui/components/lookup";
import type { Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { format } from "date-fns";
import { computed, defineComponent, ref, watch } from "vue";
import type { DustCard } from "@/views/dustCard/dustCard";
import type { DustClass } from "@/views/dustClass/dustClass";
import type { DustMonitorCard } from "@/views/dustMonitorCard/dustMonitorCard";
import type { DustMonitorClass } from "@/views/dustMonitorClass/dustMonitorClass";
import type { ElectronicForm } from "@/views/electronicForm/electronicForm";

export default defineComponent({
  name: "HomeExternalCardsAndClasses",
  components: {
    HomeExternalCardsAndClassesCard,
  },
  setup() {
    const { userRef } = useUser();
    const groups = ref(emptyCardsAndClassesGroups());
    const failedSources = ref<string[]>([]);
    const hasLoadError = computed(() => failedSources.value.length > 0);
    const isAuthenticated = computed(() => userRef.value.isAuthenticated);

    /**
     * One failing source must not blank the whole card, so each list is adapted on its own and
     * contributes nothing when it fails. Failures are recorded so the card can say so.
     */
    const itemsOrEmpty = async <T>(
      label: string,
      request: Promise<any>,
      adapter: (response: any) => Promise<any>,
    ): Promise<T[]> => {
      try {
        const adapted = (await adaptPagedResponse(await request, adapter as any)) as unknown as Paging<T>;
        return adapted._items ?? [];
      } catch (error) {
        failedSources.value.push(label);
        console.error(`Unable to load ${label} for cards and classes`, error);
        return [];
      }
    };

    const load = async () => {
      failedSources.value = [];
      const today = format(new Date(), "yyyy-MM-dd");
      try {
        const [dustCards, dustMonitorCards, dustClasses, dustMonitorClasses, registrationForms, settings] =
          await Promise.all([
          itemsOrEmpty<DustCard>(
            "dust cards",
            getDustCards({
              expirationLocalDateGreaterThanOrEqualTo: today,
              orderBy: [{ propertyName: "expirationLocalDate", isDescending: false }],
              take: 500,
            }),
            dustCardService.skinnyResponseAdapter,
          ),
          itemsOrEmpty<DustMonitorCard>(
            "dust monitor cards",
            getDustMonitorCards({
              orderBy: [{ propertyName: "expirationLocalDate", isDescending: false }],
              take: 500,
            }),
            dustMonitorCardService.skinnyResponseAdapter,
          ),
          itemsOrEmpty<DustClass>(
            "dust classes",
            getDustClasses({
              classLocalDateGreaterThanOrEqualTo: today,
              orderBy: [
                { propertyName: "classLocalDate", isDescending: false },
                { propertyName: "classLocalTime", isDescending: false },
              ],
              take: 500,
            }),
            dustClassService.skinnyResponseAdapter,
          ),
          itemsOrEmpty<DustMonitorClass>(
            "dust monitor classes",
            getDustMonitorClasses({
              classLocalDateGreaterThanOrEqualTo: today,
              orderBy: [
                { propertyName: "classLocalDate", isDescending: false },
                { propertyName: "classLocalTime", isDescending: false },
              ],
              take: 500,
            }),
            dustMonitorClassService.skinnyResponseAdapter,
          ),
          itemsOrEmpty<ElectronicForm>(
            "class registrations",
            getElectronicForms({
              formMurnIn: classRegistrationFormMurns,
              isParent: true,
              isSubmittedByCurrentUser: true,
              orderBy: [{ propertyName: "submissionDateTime", isDescending: true }],
              take: 500,
            }),
            electronicFormService.responseAdapter,
          ),
            ensureApplicationSettings().catch((error) => {
              console.error("Unable to load application settings for cards and classes", error);
              return null;
            }),
          ]);

        const cardValidityMs = settings?.get(ApplicationSettingKeys.CardValidity)?.durationMilliseconds;
        groups.value = buildCardsAndClassesGroups({
          dustCards,
          dustMonitorCards,
          dustClasses,
          dustMonitorClasses,
          registrationForms,
          userEmail: userRef.value.email ?? "",
          expiresSoonLeadMs: cardValidityMs != null ? cardValidityMs / 6 : undefined,
        });
      } catch (error) {
        groups.value = emptyCardsAndClassesGroups();
        failedSources.value.push("cards and classes");
        console.error("Unable to build cards and classes", error);
      }
    };

    // Sign-in can settle after mount, so follow the flag rather than loading once.
    watch(
      isAuthenticated,
      (authenticated) => {
        if (authenticated) {
          load();
        } else {
          groups.value = emptyCardsAndClassesGroups();
          failedSources.value = [];
        }
      },
      { immediate: true },
    );

    return {
      groups,
      hasLoadError,
    };
  },
});
</script>
